import React, { useRef } from 'react';
import { Box, TextField, Typography, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const ImpugnacaoCreditoLiquidacaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Impugnação de Crédito em Liquidação Extrajudicial"
        customDescription="Impugnação de crédito habilitado em procedimento de liquidação extrajudicial de instituição financeira"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
        />

        {/* 2. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos do crédito em liquidação extrajudicial."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 3. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva os fundamentos da impugnação, como erro na classificação do crédito, valor incorreto, documentação insuficiente, etc..."
        />

        {/* 4. Peça de referência */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Indique a peça do processo que deseja rebater ou responder: <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex. Petição inicial feita na data de 21/05/2025"
            value={values.peca_rebater || ''}
            onChange={(e) => handleFieldChange('peca_rebater', e.target.value)}
            required
          />
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os detalhes da impugnação que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="A impugnação de crédito em liquidação extrajudicial segue o rito da Lei 6.024/74. Deve-se demonstrar erro na habilitação, classificação ou valor do crédito. Anexe documentos que comprovem a incorreção alegada. O prazo para impugnação deve ser observado conforme publicação do quadro geral de credores." />
    </Box>
  );
};

export default ImpugnacaoCreditoLiquidacaoForm;

